import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import EpisodeCard from "@/components/feature/EpisodeCard";
import CharacterCard from "@/components/feature/CharacterCard";
import ReviewCard from "@/components/feature/ReviewCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { episodes, type Episode } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { reviews } from "@/mocks/reviews";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const featuredIds = ["ep-03", "ep-08", "ep-09", "ep-10"];
const heroIds = ["ep-01", "ep-09", "ep-10"];

export default function Home() {
  useDocumentMeta({
    title: "美国式禁忌2-影视剧介绍与最新剧情解析",
    description:
      "美国式禁忌2为您提供详细的影视剧介绍、剧情解析及角色信息，涵盖剧情亮点和观众点评，帮助您全面了解这部电视剧。",
    keywords:
      "美国式禁忌2,美国式禁忌2剧情,美国式禁忌2介绍,美国式禁忌2角色,美国式禁忌2剧评,美剧推荐",
  });
  const heroEpisodes = useMemo(
    () =>
      heroIds
        .map((id) => episodes.find((episode) => episode.id === id))
        .filter((episode): episode is Episode => Boolean(episode)),
    []
  );

  const featured = useMemo(
    () =>
      featuredIds
        .map((id) => episodes.find((episode) => episode.id === id))
        .filter((episode): episode is Episode => Boolean(episode)),
    []
  );

  const latest = useMemo(
    () => [...episodes].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 5),
    []
  );

  const featuredReviews = useMemo(() => reviews.filter((review) => review.featured).slice(0, 4), []);
  const mainCharacters = useMemo(() => characters.slice(0, 4), []);

  const episodeLabel = (id: string) => {
    const episode = episodes.find((item) => item.id === id);
    return episode ? `第 ${episode.number} 集 ${episode.title}` : "相关剧集";
  };

  return (
    <SiteLayout>
      <HeroCarousel episodes={heroEpisodes} />

      <section className="max-w-[1200px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="Deep Dive"
          title="精选剧情解析"
          description="从人物动机到镜头调度，逐集拆解《美国式禁忌2》中最值得反复品味的段落。"
          action={
            <Link
              to="/category"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300 hover:border-accent-500 text-foreground-800 hover:text-accent-300 text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              全部解析
              <i className="ri-arrow-right-line" />
            </Link>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {featured.map((episode) => (
            <Link
              key={episode.id}
              to={`/detail/${episode.id}`}
              className="group relative rounded-lg overflow-hidden border border-background-200 hover:border-primary-600 transition-colors cursor-pointer"
            >
              <div className="relative w-full h-52 md:h-60 overflow-hidden bg-background-200">
                <img
                  src={episode.still}
                  alt={`美国式禁忌2 第${episode.number}集剧情解析配图`}
                  title={`第${episode.number}集 ${episode.title} 剧情解析`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent" />
              </div>
              <div className="relative -mt-16 p-5">
                <span className="inline-flex items-center h-6 px-2.5 rounded-full bg-accent-500/15 text-accent-400 text-xs font-medium mb-3">
                  第 {episode.number} 集 · 深度解析
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950 group-hover:text-accent-300 transition-colors">
                  {episode.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {episode.tagline}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section id="characters" className="bg-background-100 border-y border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="Characters"
            title="角色信息"
            description="每个角色都背着自己的那部分真相。点击卡片，查看与该角色相关的剧集内容。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {mainCharacters.map((character) => (
              <CharacterCard key={character.id} character={character} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="Updates"
          title="最新剧集更新"
          description="按上线时间排序，追踪最新上线的剧情与解析。"
          action={
            <Link
              to="/category"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              查看全部剧集
              <i className="ri-arrow-right-line" />
            </Link>
          }
        />
        <div className="flex flex-col gap-4">
          {latest.map((episode) => (
            <EpisodeCard key={episode.id} episode={episode} variant="horizontal" />
          ))}
        </div>
      </section>

      <section id="reviews" className="bg-background-100 border-y border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="Reviews"
            title="观众点评精选"
            description="来自影迷的真实观感。点赞与评论为本站前端演示功能，用于展示互动效果。"
          />
          {featuredReviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  showEpisode
                  episodeLabel={episodeLabel(review.episodeId)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-14 rounded-lg border border-dashed border-background-300">
              <p className="font-heading text-base text-foreground-800">暂无点评，欢迎参与分享</p>
              <Link
                to="/feedback"
                className="inline-flex items-center gap-2 mt-4 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                我要发言
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="rounded-lg overflow-hidden border border-background-200 bg-gradient-to-br from-primary-950 to-background-100 p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-accent-400 mb-3">
                About This Site
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                一个只聊《美国式禁忌2》的深度内容站
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                我们用分集解析、角色档案、镜头语言拆解与观众点评，陪你重新看懂这个家族是如何一步步走到沉默的尽头。所有内容均为影视作品介绍与观众讨论，不提供任何影片资源。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  了解本站
                </Link>
                <Link
                  to="/feedback"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 hover:border-accent-500 text-foreground-800 hover:text-accent-300 text-sm whitespace-nowrap cursor-pointer transition-colors"
                >
                  给我们留言
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
              {[
                { value: `${episodes.length}`, label: "已收录剧集" },
                { value: `${characters.length}`, label: "角色档案" },
                { value: "每周", label: "内容更新" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-md bg-background-50/60 border border-background-200 px-4 py-3 text-center lg:text-left"
                >
                  <p className="font-heading text-xl md:text-2xl font-bold text-accent-400">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-600 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}